/**
 * BackorderAwardPOPanel
 * ---------------------
 * Post-award workflow controls for the Admin backorder detail drawer:
 *
 *   1. Awarded Supplier section
 *        • Dropdown of existing suppliers OR Manual Entry toggle
 *        • "Email Supplier Award Notice" button (auto-advances status → awarded)
 *
 *   2. PO Number inline editor (editable only once status ≥ awarded)
 *
 *   3. Estimated Lead Time dropdown (revealed after PO saved)
 *        • 24-48 Hours · 3-5 Business Days · 1-2 Weeks · Custom Date
 *
 *   4. "Email PO Acknowledgment" button — locked until valid PO is saved
 *        • Sends transactional confirmation to hospital requestor (To) +
 *          any manually added contact emails (Cc).
 *
 * All three workflows write through their dedicated endpoints in
 * /api/admin/backorders/{id}/{...} and call `onRefresh` so the parent
 * drawer re-fetches the backorder doc afterward.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Award, Mail, Save, CheckCircle2, AlertCircle, Loader2, X, Plus,
  Users, Paperclip, Upload, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CrmContactPicker from "@/components/admin/CrmContactPicker";

const API = process.env.REACT_APP_BACKEND_URL;

const LEAD_TIME_OPTIONS = [
  { code: "24-48-hours",       label: "24-48 Hours" },
  { code: "3-5-business-days", label: "3-5 Business Days" },
  { code: "1-2-weeks",         label: "1-2 Weeks" },
  { code: "custom",            label: "Custom Date" },
];

const POST_AWARD_STATUSES = new Set(["awarded", "fulfilled", "shipped", "delayed", "delivered"]);

export default function BackorderAwardPOPanel({ backorder, token, onRefresh }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isPostAward = POST_AWARD_STATUSES.has((backorder.status || "").toLowerCase());

  // ------- Supplier Award state -------
  const [suppliers, setSuppliers] = useState([]);
  const [manualMode, setManualMode] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [awarding, setAwarding] = useState(false);
  // Default: send the formal award email. Admin can opt out for cases where
  // the supplier was already notified verbally / via PO / via portal chat.
  const [sendAwardEmail, setSendAwardEmail] = useState(true);
  // Multi-award: admin can pick specific line items to award to this supplier.
  // Empty selection == "all remaining lines" (server treats it the same way).
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [deletingAwardId, setDeletingAwardId] = useState(null);

  const awards = backorder.awards || [];
  const items = backorder.items || [];
  // Set of line_ids that have already been awarded on some prior award.
  const alreadyAwardedLineIds = new Set(
    awards.flatMap((a) => a.item_line_ids || []),
  );

  useEffect(() => {
    if (manualMode) return;
    axios.get(`${API}/api/suppliers`, { headers })
      .then((r) => setSuppliers(r.data?.suppliers || r.data || []))
      .catch(() => setSuppliers([]));
  }, [manualMode, headers]);

  const onPickSupplier = (id) => {
    setSupplierId(id);
    const s = suppliers.find((x) => x.id === id);
    if (s) {
      setSupplierName(s.name || s.contact_name || "");
      setSupplierEmail(s.email || "");
    } else {
      setSupplierName("");
      setSupplierEmail("");
    }
  };

  const sendAwardNotice = async () => {
    if (!supplierName.trim() || !supplierEmail.trim()) {
      toast.error("Pick a supplier or fill in name + email.");
      return;
    }
    setAwarding(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/email-supplier-award`,
        {
          supplier_id: supplierId || null,
          supplier_name: supplierName,
          supplier_email: supplierEmail,
          send_email: sendAwardEmail,
          item_line_ids: selectedLineIds,
        },
        { headers },
      );
      const advanced = r.data?.status_advanced_to_awarded;
      const emailSent = r.data?.email_sent !== false;
      const created = r.data?.supplier_created;
      if (created) {
        // Manual entries are real supplier records now — show them in the
        // dropdown straight away instead of making the admin reload.
        setSuppliers((list) => [...list, created]);
        toast.success(`${created.name} saved to your supplier list`, {
          description: "Pick them from the dropdown next time — no email was sent to them.",
          duration: 8000,
        });
      }
      const linesDesc = selectedLineIds.length > 0
        ? `${selectedLineIds.length} line${selectedLineIds.length === 1 ? "" : "s"} awarded`
        : "all lines";
      toast.success(
        advanced
          ? (emailSent ? `Award notice sent · ${linesDesc} · status → Awarded` : `Award recorded · ${linesDesc} · status → Awarded`)
          : (emailSent ? `Award notice sent to ${supplierEmail} (${linesDesc})` : `Award recorded for ${supplierName} · ${linesDesc}`),
        emailSent
          ? { description: r.data?.email_status === "sent" ? "Email delivered." : "Email queued (Outlook/SMTP)." }
          : { description: "No email sent — supplier stamped only." },
      );
      // Clear the form so admin can immediately award another supplier
      setSupplierId("");
      setSupplierName("");
      setSupplierEmail("");
      setSelectedLineIds([]);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send award notice.");
    } finally {
      setAwarding(false);
    }
  };

  const deleteAward = async (award) => {
    if (!window.confirm(`Remove award for ${award.supplier_name} (${award.supplier_email})? This does NOT send any email.`)) return;
    setDeletingAwardId(award.id);
    try {
      await axios.delete(
        `${API}/api/admin/backorders/${backorder.id}/awards/${award.id}`,
        { headers },
      );
      toast.success(`Award removed for ${award.supplier_name}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove award.");
    } finally {
      setDeletingAwardId(null);
    }
  };

  const toggleLineId = (lineId) => {
    setSelectedLineIds((prev) =>
      prev.includes(lineId) ? prev.filter((x) => x !== lineId) : [...prev, lineId]
    );
  };

  // ------- PO Number state -------
  const [poNumber, setPoNumber] = useState(backorder.po_number || "");
  const [poDirty, setPoDirty] = useState(false);
  const [poSaving, setPoSaving] = useState(false);
  const savedPo = backorder.po_number || "";

  useEffect(() => {
    setPoNumber(backorder.po_number || "");
    setPoDirty(false);
  }, [backorder.id, backorder.po_number]);

  const savePo = async () => {
    if (!isPostAward) {
      toast.error("Set status to Awarded before adding a PO.");
      return;
    }
    setPoSaving(true);
    try {
      await axios.patch(
        `${API}/api/admin/backorders/${backorder.id}/po`,
        { po_number: poNumber.trim() || null },
        { headers },
      );
      toast.success("PO saved.");
      setPoDirty(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save PO.");
    } finally {
      setPoSaving(false);
    }
  };

  // ------- Lead Time + PO Ack Email state -------
  const [leadCode, setLeadCode] = useState(backorder.po_acknowledgment_lead_code || "3-5-business-days");
  const [leadCustom, setLeadCustom] = useState("");
  const [extraEmails, setExtraEmails] = useState([]); // [{email}]
  const [newExtra, setNewExtra] = useState("");
  const [sendingAck, setSendingAck] = useState(false);
  const [crmPicker, setCrmPicker] = useState(null);        // "to" | "cc" | null
  const [ackTo, setAckTo] = useState(
    backorder.contact_email || backorder.hospital_email || "");
  const [ackFiles, setAckFiles] = useState(backorder.order_form_attachments || []);
  const [ackFileIds, setAckFileIds] = useState(
    (backorder.order_form_attachments || []).map((a) => a.id));
  const [uploadingAck, setUploadingAck] = useState(false);
  const [draftingAck, setDraftingAck] = useState(false);

  const ackLocked = !savedPo;  // disabled until a real, saved PO exists

  const addExtra = () => {
    const e = newExtra.trim().toLowerCase();
    if (!e.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    if (extraEmails.some((x) => x === e)) return;
    setExtraEmails([...extraEmails, e]);
    setNewExtra("");
  };

  const addFromCrm = (contacts, target) => {
    const emails = (contacts || []).map((c) => (c.email || "").trim().toLowerCase()).filter(Boolean);
    if (!emails.length) return;
    if (target === "cc") {
      setExtraEmails((prev) => Array.from(new Set([...prev, ...emails])));
    } else {
      setAckTo((prev) => {
        const have = (prev || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
        return Array.from(new Set([...have, ...emails])).join(", ");
      });
    }
    toast.success(`${emails.length} CRM contact${emails.length === 1 ? "" : "s"} added to ${target.toUpperCase()}`);
  };

  const uploadAckForm = async (file) => {
    if (!file) return;
    setUploadingAck(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/customer-order/attachments`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setAckFiles((a) => [...a, r.data.attachment]);
      setAckFileIds((ids) => [...ids, r.data.attachment.id]);
      toast.success(`${r.data.attachment.name} will ride along with the acknowledgment`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingAck(false); }
  };

  const ackBodyText = () => {
    const name = (backorder.contact_name || backorder.hospital_name || "there").trim();
    const lead = leadCode === "custom"
      ? leadCustom.trim()
      : (LEAD_TIME_OPTIONS.find((o) => o.code === leadCode)?.label || "");
    return (
      (savedPo ? `PO #${savedPo}\n\n` : "") +
      `Hi ${name},\n\n` +
      `Thank you for your order — we have it and it is being processed. ` +
      `Your sales order confirmation is attached.\n\n` +
      (lead ? `Estimated ship lead time: ${lead}.\n\n` : "") +
      `I will send tracking as soon as it becomes available.\n\n` +
      `Kind Regards,\nGeorge`
    );
  };

  // Writes the acknowledgment into Outlook Drafts with the QuickBooks sales
  // order already attached — a mailto: link cannot carry a file.
  const draftAckInOutlook = async () => {
    const to_emails = (ackTo || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!to_emails.length) { toast.error("Add at least one To recipient."); return; }
    setDraftingAck(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/customer-order/outlook-draft`,
        {
          to_emails,
          cc_emails: extraEmails,
          subject: savedPo ? `Order acknowledgment — PO #${savedPo}`
                           : `Order acknowledgment — ${backorder.id}`,
          body_text: ackBodyText(),
          attachment_ids: ackFileIds,
          mark_confirmed: false,
        }, { headers });
      if (r.data?.web_link) window.open(r.data.web_link, "_blank", "noopener");
      toast.success(`Draft waiting in Outlook (${r.data?.mailbox || "orders@sterilemate.com"})`, {
        description: r.data?.attachment_count
          ? `${r.data.attachment_count} form(s) already attached — just press Send.`
          : "No form attached — press Send when you're ready.",
        duration: 8000,
      });
      onRefresh?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      if (e.response?.status === 503) {
        toast.error("Outlook drafting is not available yet", { description: msg, duration: 12000 });
      } else {
        toast.error(msg || "Could not create the Outlook draft");
      }
    } finally { setDraftingAck(false); }
  };

  const sendPoAck = async () => {
    if (ackLocked) {
      toast.error("Save a PO number first.");
      return;
    }
    if (leadCode === "custom" && !leadCustom.trim()) {
      toast.error("Enter a custom lead time (e.g. 'May 22, 2026').");
      return;
    }
    setSendingAck(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/email-po-acknowledgment`,
        {
          po_number: savedPo,
          lead_time_code: leadCode,
          lead_time_custom: leadCode === "custom" ? leadCustom.trim() : null,
          additional_emails: extraEmails,
        },
        { headers },
      );
      toast.success(`PO acknowledgment sent`, {
        description: `To: ${r.data?.recipients?.to}` + (r.data?.recipients?.cc?.length ? ` · Cc: ${r.data.recipients.cc.length}` : ""),
      });
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send acknowledgment.");
    } finally {
      setSendingAck(false);
    }
  };

  return (
    <section className="bg-gradient-to-br from-violet-50 to-blue-50 border border-violet-200 rounded-xl p-4 space-y-4" data-testid="award-po-panel">
      <header className="flex items-center gap-2">
        <Award className="w-4 h-4 text-violet-700" />
        <h3 className="text-sm font-bold text-violet-900 uppercase tracking-wider">Award & PO Workflow</h3>
      </header>

      {/* ============== 1. Awarded Suppliers (multi) =========================*/}
      <div className="bg-white rounded-lg p-3 border border-violet-100">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Awarded Suppliers {awards.length > 0 && <span className="ml-1 text-violet-700">({awards.length})</span>}
          </h4>
          <button
            type="button"
            onClick={() => setManualMode((v) => !v)}
            className="text-xs font-semibold text-violet-700 hover:underline"
            data-testid="award-supplier-toggle-manual"
          >
            {manualMode ? "← Use directory" : "Manual entry →"}
          </button>
        </div>

        {/* Existing awards list */}
        {awards.length > 0 && (
          <ul className="mb-3 space-y-1.5" data-testid="award-list">
            {awards.map((a) => {
              const scopeLabel = (a.item_line_ids || []).length === 0
                ? "All items"
                : `${a.item_line_ids.length} of ${items.length} line${items.length === 1 ? "" : "s"}`;
              const awardedItems = (a.item_line_ids || [])
                .map((lid) => items.find((it) => it.line_id === lid))
                .filter(Boolean);
              return (
                <li
                  key={a.id}
                  className="flex items-start justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5"
                  data-testid={`award-chip-${a.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                      <CheckCircle2 className="w-3 h-3" />
                      <span className="truncate">{a.supplier_name}</span>
                      <span className="text-emerald-600 font-normal">·</span>
                      <span className="text-emerald-600 font-normal truncate">{a.supplier_email}</span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-emerald-700 flex flex-wrap items-center gap-1.5">
                      <span className="font-semibold">{scopeLabel}</span>
                      {awardedItems.length > 0 && (
                        <span className="text-emerald-600">·</span>
                      )}
                      {awardedItems.slice(0, 3).map((it, idx) => (
                        <span key={idx} className="bg-white border border-emerald-200 rounded px-1 py-[1px] font-mono">
                          {it.sku || it.name?.slice(0, 24) || `#${idx + 1}`}
                        </span>
                      ))}
                      {awardedItems.length > 3 && (
                        <span className="text-emerald-600">+{awardedItems.length - 3} more</span>
                      )}
                      {a.email_sent ? (
                        <span className="text-emerald-600 ml-1"><Mail className="inline w-2.5 h-2.5" /> emailed</span>
                      ) : (
                        <span className="text-slate-500 ml-1 italic">no email</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteAward(a)}
                    disabled={deletingAwardId === a.id}
                    className="text-rose-500 hover:text-rose-700 hover:bg-rose-100 rounded p-1 flex-shrink-0"
                    title="Remove this award"
                    data-testid={`award-remove-${a.id}`}
                  >
                    {deletingAwardId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* Add new award form */}
        <div className="border-t border-slate-100 pt-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
            <Plus className="w-3 h-3" /> {awards.length === 0 ? "Award a supplier" : "Add another award"}
          </div>
          {!manualMode ? (
            <select
              value={supplierId}
              onChange={(e) => onPickSupplier(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              data-testid="award-supplier-dropdown"
            >
              <option value="">— Select a supplier —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.contact_name || s.company_name} {s.email ? `· ${s.email}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Supplier name"
                className="text-sm"
                data-testid="award-supplier-manual-name"
              />
              <Input
                value={supplierEmail}
                onChange={(e) => setSupplierEmail(e.target.value)}
                placeholder="supplier@company.com"
                type="email"
                className="text-sm"
                data-testid="award-supplier-manual-email"
              />
              <p className="col-span-2 text-[10px] text-emerald-800">
                Whoever you type here is saved to your supplier list permanently — no portal invite
                email, and no retyping them next month.
              </p>
            </div>
          )}

          {/* Line-item selector — appears only if the BO has line items with line_ids */}
          {items.length > 0 && items.some((it) => it.line_id) && (
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-2" data-testid="award-line-picker">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Award which lines to this supplier?
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedLineIds([])}
                  className="text-[10px] font-semibold text-slate-500 hover:text-slate-800"
                  data-testid="award-lines-clear"
                >
                  {selectedLineIds.length === 0 ? "(all lines by default)" : "clear"}
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-auto">
                {items.map((it, idx) => {
                  const lid = it.line_id;
                  if (!lid) return null;
                  const alreadyAwarded = alreadyAwardedLineIds.has(lid);
                  const checked = selectedLineIds.includes(lid);
                  return (
                    <label
                      key={lid}
                      className={`flex items-center gap-2 text-xs px-1.5 py-1 rounded cursor-pointer ${
                        alreadyAwarded ? "bg-amber-50 text-amber-800" : "hover:bg-white"
                      }`}
                      data-testid={`award-line-row-${idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleLineId(lid)}
                        className="h-3 w-3"
                        data-testid={`award-line-check-${idx}`}
                      />
                      <span className="font-mono text-[10px] text-slate-500 w-16 shrink-0">{it.sku || "—"}</span>
                      <span className="flex-1 truncate">{it.name || "(no name)"}</span>
                      <span className="text-slate-600 font-mono text-[10px] shrink-0">
                        {it.quantity} {it.uom || "Each"}
                      </span>
                      {alreadyAwarded && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 shrink-0" title="Already on a prior award">
                          split
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <p className="mt-1.5 text-[10px] text-slate-500 italic">
                Leave all boxes unchecked to award every line to this supplier. Check specific rows to split a BO across multiple suppliers.
              </p>
            </div>
          )}

          <label className="mt-3 flex items-center gap-2 text-xs text-slate-700 select-none cursor-pointer" data-testid="award-send-email-label">
            <input
              type="checkbox"
              checked={sendAwardEmail}
              onChange={(e) => setSendAwardEmail(e.target.checked)}
              className="accent-violet-600 h-3.5 w-3.5"
              data-testid="award-send-email-checkbox"
            />
            <span>
              Send award email to supplier
              {!sendAwardEmail && (
                <span className="ml-1 text-amber-700 italic">(supplier will be stamped, no email)</span>
              )}
            </span>
          </label>

          <Button
            size="sm"
            onClick={sendAwardNotice}
            disabled={awarding || !supplierName.trim() || (sendAwardEmail && !supplierEmail.trim())}
            className="mt-2 bg-violet-600 hover:bg-violet-700 gap-1.5 text-xs"
            data-testid="award-send-notice-btn"
          >
            {awarding
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : (sendAwardEmail ? <Mail className="w-3.5 h-3.5" /> : <Award className="w-3.5 h-3.5" />)}
            {sendAwardEmail
              ? (awards.length === 0 ? "Email Supplier Award Notice" : "Add Award + Email Supplier")
              : (awards.length === 0 ? "Record Award (No Email)" : "Add Award (No Email)")}
          </Button>
        </div>
      </div>

      {/* ============== 2. PO Number =========================================*/}
      <div className="bg-white rounded-lg p-3 border border-violet-100">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Hospital Purchase Order</h4>
        {!isPostAward ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 inline-flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" />
            PO entry unlocks once status is set to <strong>Awarded</strong>.
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              value={poNumber}
              onChange={(e) => { setPoNumber(e.target.value); setPoDirty(e.target.value !== savedPo); }}
              placeholder="PO #"
              className="text-sm font-mono"
              data-testid="award-po-input"
            />
            <Button
              size="sm"
              onClick={savePo}
              disabled={poSaving || !poDirty}
              variant={poDirty ? "default" : "outline"}
              className="gap-1.5 text-xs"
              data-testid="award-po-save-btn"
            >
              {poSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {poDirty ? "Save" : "Saved"}
            </Button>
          </div>
        )}
      </div>

      {/* ============== 3. Lead Time + 4. PO Ack Email =======================*/}
      <div className={`bg-white rounded-lg p-3 border ${ackLocked ? "border-slate-200 opacity-70" : "border-violet-100"}`}>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">PO Acknowledgment Email</h4>

        {ackLocked && (
          <p className="text-xs text-slate-500 mb-2">
            Save a PO number above to unlock the acknowledgment email.
          </p>
        )}

        <label className="text-xs font-semibold text-slate-600 block mb-1">Estimated Ship Lead Time</label>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select
            disabled={ackLocked}
            value={leadCode}
            onChange={(e) => setLeadCode(e.target.value)}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm disabled:bg-slate-50"
            data-testid="award-lead-time-select"
          >
            {LEAD_TIME_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>{o.label}</option>
            ))}
          </select>
          {leadCode === "custom" && (
            <Input
              disabled={ackLocked}
              type="date"
              value={leadCustom}
              onChange={(e) => setLeadCustom(e.target.value)}
              className="text-sm"
              data-testid="award-lead-time-custom"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mb-1">
          <label className="text-xs font-semibold text-slate-600">Acknowledgment to</label>
          <Button type="button" size="sm" variant="ghost"
            className="h-6 px-2 text-[10px] font-bold text-blue-700 hover:bg-blue-50"
            onClick={() => setCrmPicker("to")}
            data-testid="award-ack-crm-to-btn">
            <Users className="w-3 h-3 mr-1" /> Pick from CRM
          </Button>
        </div>
        <Input
          value={ackTo}
          onChange={(e) => setAckTo(e.target.value)}
          placeholder="buyer@hospital.org"
          className="text-sm mb-2"
          data-testid="award-ack-to-input"
        />

        <div className="flex items-center justify-between gap-2 mb-1">
          <label className="text-xs font-semibold text-slate-600">Additional contacts (Cc)</label>
          <Button type="button" size="sm" variant="ghost"
            className="h-6 px-2 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
            onClick={() => setCrmPicker("cc")}
            data-testid="award-ack-crm-cc-btn">
            <Users className="w-3 h-3 mr-1" /> Pick from CRM
          </Button>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Input
            disabled={ackLocked}
            value={newExtra}
            onChange={(e) => setNewExtra(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }}
            placeholder="add@hospital.org"
            type="email"
            className="text-sm flex-1"
            data-testid="award-ack-cc-input"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={ackLocked || !newExtra.includes("@")}
            onClick={addExtra}
            className="gap-1 text-xs"
            data-testid="award-ack-cc-add-btn"
          >
            <Plus className="w-3 h-3" /> Add
          </Button>
        </div>
        {extraEmails.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {extraEmails.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-xs">
                {e}
                <button onClick={() => setExtraEmails(extraEmails.filter((x) => x !== e))} className="text-slate-400 hover:text-rose-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* QuickBooks sales order — attached to the acknowledgment. */}
        <div className="rounded-lg border border-cyan-200 bg-cyan-50/50 p-2.5 mb-3" data-testid="award-ack-attachments">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-800 inline-flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" /> Sales order to attach
            </p>
            <label className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-[11px] font-semibold text-white cursor-pointer ${uploadingAck ? "bg-slate-400" : "bg-cyan-600 hover:bg-cyan-700"}`}
              data-testid="award-ack-upload-btn">
              {uploadingAck ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploadingAck ? "Uploading…" : "Upload sales order"}
              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                disabled={uploadingAck}
                onChange={(e) => { uploadAckForm(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="award-ack-file-input" />
            </label>
          </div>
          {ackFiles.length === 0 ? (
            <p className="text-[11px] text-slate-500 mt-1.5">
              Nothing attached yet — upload the QuickBooks sales order PDF and it rides along.
            </p>
          ) : (
            <div className="mt-1.5 space-y-1">
              {ackFiles.map((att) => (
                <label key={att.id} className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={ackFileIds.includes(att.id)}
                    onChange={(e) => setAckFileIds((ids) =>
                      e.target.checked ? [...ids, att.id] : ids.filter((i) => i !== att.id))}
                    data-testid={`award-ack-attach-${att.id}`} />
                  <span className="truncate">{att.name}</span>
                  <span className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(0)} KB</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <Button
          size="sm"
          onClick={sendPoAck}
          disabled={ackLocked || sendingAck}
          className="bg-blue-600 hover:bg-blue-700 gap-1.5 text-xs"
          data-testid="award-send-po-ack-btn"
        >
          {sendingAck ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
          Email PO Acknowledgment
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={draftAckInOutlook}
          disabled={draftingAck}
          className="ml-2 border-cyan-400 text-cyan-800 hover:bg-cyan-50 gap-1.5 text-xs"
          title="Writes the acknowledgment into Outlook Drafts with the sales order already attached"
          data-testid="award-ack-outlook-draft-btn"
        >
          {draftingAck ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
          Open in Outlook (attached)
        </Button>

        {backorder.po_acknowledgment_sent_at && (
          <div className="mt-2 text-[11px] text-slate-500">
            Last sent: {new Date(backorder.po_acknowledgment_sent_at).toLocaleString()}
            {backorder.po_acknowledgment_lead_time && ` · lead time: ${backorder.po_acknowledgment_lead_time}`}
          </div>
        )}
      </div>

      {/* CRM address book for the acknowledgment recipients. */}
      <CrmContactPicker
        open={!!crmPicker}
        onOpenChange={(o) => !o && setCrmPicker(null)}
        mode="multi"
        title={crmPicker === "cc" ? "Add Cc recipients from CRM" : "Add acknowledgment recipients from CRM"}
        confirmLabel={crmPicker === "cc" ? "Add to Cc" : "Add to To"}
        onConfirm={(contacts) => { addFromCrm(contacts, crmPicker || "to"); setCrmPicker(null); }}
      />
    </section>
  );
}
